import { useCallback, useEffect, useState, useRef } from "react";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { COLUMN_HEADER_KEYS } from "globals/constants";
import { useGroupRoles } from "hooks";
import { api } from "services";
import { notifyError } from "utils";

const debuggerPrefix = "useAdminTeam >> ";

export const useAdminTeam = (initialTeam, formType, formId) => {

	const members = useRef([ ...initialTeam ]);
	const lastSavedTeam = useRef([ ...initialTeam ]);
	const [ mergedMembers, setMergedMembers ] = useState([]);
	const [ isSaving, setIsSaving ] = useState(false);

	const resetChanges = useCallback(() => {
		members.current = [ ...initialTeam ];
		setMergedMembers(mergeMembers());
	}, [ initialTeam ]);

	const setMembers = useCallback(updatedMembers => {
		members.current = updatedMembers;
		setMergedMembers(mergeMembers());
	}, [ members ]);

	const { getGroupRoleByName } = useGroupRoles();

	const normalizePerson = person => {
		const combineNameParts = () => [ person.firstName, person.middleName, person.lastName ]
			.filter(name => isNonEmptyString(name))
			.join(" ");

		const fullName = isNonEmptyString(person.fullName)
			? person.fullName
			: combineNameParts();

		const personId = isNonEmptyString(person.personId)
			? person.personId
			: person.id;

		const contactEmail = isNonEmptyString(person.contactEmail)
			? person.contactEmail
			: "";

		return { personId, fullName, contactEmail };
	};

	const formatPersonWithRole = useCallback((person, role) => {
		const { personId, fullName, contactEmail } = normalizePerson(person);
		const { id: roleId, name: roleName } = getGroupRoleByName(role);

		return { personId, fullName, roleId, roleName, contactEmail };
	}, [ getGroupRoleByName ]);

	const addMember = useCallback((person, role) => {
		const newMember = formatPersonWithRole(person, role);
		let isMemberWithRoleInGroup = false;

		const updatedMembers = members.current.map(member => {
			const isSamePerson = member.personId === newMember.personId;
			const isSameRole = member.roleName === role;

			if (isSamePerson && isSameRole) {
				isMemberWithRoleInGroup = true;
			}

			return { ...member };
		});

		if (!isMemberWithRoleInGroup) {
			updatedMembers.push(newMember);
		}

		setMembers(updatedMembers);
	}, [ formatPersonWithRole, members, setMembers ]);

	/**
	 * @param person - string id or obj with "personId" key
	 * @param role - string name of the role to remove - if empty, will unassign all roles for given person
	 */
	const removeMember = useCallback((person, role) => {
		const hasRole = isNonEmptyString(role);
		const personIdToRemove = isNonEmptyString(person)
			? person
			: person?.personId || person?.id || undefined;

		if (!personIdToRemove) {
			console.warn(`${debuggerPrefix} Asked to remove invalid team member: `, person);

			return;
		}

		const updatedMembers = members.current.filter(({ personId, roleName }) => {
			if (hasRole) {
				return personId !== personIdToRemove || roleName !== role;
			}

			return personId !== personIdToRemove;
		});

		setMembers(updatedMembers);
	}, [ members, setMembers ]);

	const save = useCallback(async () => {
		let request;
		switch (formType) {
			case COLUMN_HEADER_KEYS.PROPOSAL: 
				request = api.proposal.updateAdminTeamMember; 
				break;
			case COLUMN_HEADER_KEYS.AWARD: 
				request = api.awards.updateAdminTeamMember; 
				break;
			case COLUMN_HEADER_KEYS.PROJECT: 
				request = api.project.updateAdminTeamMember; 
				break;
			default:
        		throw new Error(`Invalid form type: ${formType}`);
		}

		setIsSaving(true);

		try {
			await request({ formId }, members.current);
			lastSavedTeam.current = [ ...members.current ];

		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);

		} finally {
			setIsSaving(false);
		}
	}, [ members, formType, formId ]);

	const mergeMembers = () => {
		const reduceMembers = () => members.current.reduce((merged, member) => {
			const personId = member.personId;
			const targetEntryIndex = merged.findIndex(({ personId }) => personId === member.personId);
			const isPersonInGroup = targetEntryIndex > -1;
			const formattedRole = {
				id: member.id,
				roleId: member.roleId,
				roleName: member.roleName
			};

			if (isPersonInGroup) {
				merged[targetEntryIndex].roles.push(formattedRole);
			} else {
				merged.push({
					personId,
					contactEmail: member.contactEmail || "",
					fullName: member.fullName,
					roles: [ formattedRole ]
				});
			}

			return merged;
		}, []);

		return isNonEmptyArray(members.current)
			? reduceMembers()
			: members.current;
	};

	const findMemberRole = (member, targetRoleName) => {
		return member.roles.find(({ roleName }) => roleName === targetRoleName);
	};

	useEffect(() => {
		const hasInitialTeam = isNonEmptyArray(initialTeam);
		const hasCurrentTeam = isNonEmptyArray(members.current);

		if (hasInitialTeam && !hasCurrentTeam) {
			members.current = [ ...initialTeam ];
		}

		setMergedMembers(mergeMembers());
	}, [ initialTeam ]);

	return {
		addMember,
		findMemberRole,
		isSaving,
		members: mergedMembers,
		removeMember,
		resetChanges,
		save,
		teamStructure: members.current,
	};
};
